'use client';

/**
 * LoyaltyAppRoles — owner vs. employee.
 *
 * Two-column comparison lifted from Part 15 65 ("Owner vs Employee").
 * Critical for the "BYOD on staff phones is safe" argument: employees
 * only see what they need to scan + reward; owners see analytics and
 * the full database.
 */

import React from 'react';
import { Locale } from '../../../../i18n-config';
import SectionLabel from '../home/SectionLabel';
import { useTranslation } from '@/i18n/client';
import { HiCheckCircle, HiUser, HiUserGroup, HiXCircle } from 'react-icons/hi2';

type Props = { lang: Locale };

const LoyaltyAppRoles = ({ lang }: Props) => {
  const { t } = useTranslation(lang);
  const ownerCan = t('LoyaltyApp/Roles/owner_can', { returnObjects: true }) as string[];
  const empCan = t('LoyaltyApp/Roles/employee_can', { returnObjects: true }) as string[];
  const empCant = t('LoyaltyApp/Roles/employee_cant', { returnObjects: true }) as string[];

  return (
    <section
      id="roles"
      className="px-6 py-24 lg:py-28"
      style={{
        background: '#0E1130',
        borderTop: '1px solid rgba(255,255,255,0.05)',
        color: '#F5F7FA',
      }}
    >
      <div className="mx-auto max-w-[1200px]">
        <SectionLabel num="02" label={t('LoyaltyApp/Roles/badge')} />
        <h2
          className="font-bold tracking-tight"
          style={{
            fontSize: 'clamp(28px, 3.4vw, 48px)',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            margin: '12px 0 14px',
            maxWidth: 820,
          }}
        >
          {t('LoyaltyApp/Roles/title1')} <span style={{ color: '#B4C1D8' }}>{t('LoyaltyApp/Roles/title2')}</span>
        </h2>
        <p
          style={{
            color: '#B4C1D8',
            fontSize: 18,
            maxWidth: 720,
            lineHeight: 1.5,
            margin: '0 0 48px',
          }}
        >
          {t('LoyaltyApp/Roles/subtitle')}
        </p>

        <div className="grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
          <article
            className="rounded-2xl p-7"
            style={{
              background: 'rgba(34,211,238,0.06)',
              border: '1px solid rgba(34,211,238,0.30)',
            }}
          >
            <div
              className="inline-flex items-center justify-center rounded-lg mb-4"
              style={{
                width: 40,
                height: 40,
                background: 'rgba(34,211,238,0.12)',
                border: '1px solid rgba(34,211,238,0.30)',
              }}
            >
              <HiUser style={{ width: 20, height: 20, color: '#22D3EE' }} />
            </div>
            <div
              className="mono"
              style={{
                fontSize: 11,
                letterSpacing: 1.5,
                color: '#22D3EE',
                textTransform: 'uppercase',
                marginBottom: 8,
              }}
            >
              {t('LoyaltyApp/Roles/owner_label')}
            </div>
            <h3
              className="font-semibold"
              style={{
                fontSize: 19,
                color: '#F5F7FA',
                margin: '0 0 14px',
                letterSpacing: '-0.01em',
              }}
            >
              {t('LoyaltyApp/Roles/owner_title')}
            </h3>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {ownerCan.map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3"
                  style={{
                    padding: '8px 0',
                    borderTop: i === 0 ? 'none' : '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <HiCheckCircle
                    className="flex-shrink-0"
                    style={{ width: 16, height: 16, color: '#22D3EE', marginTop: 3 }}
                  />
                  <span style={{ color: '#B4C1D8', fontSize: 14, lineHeight: 1.5 }}>{item}</span>
                </li>
              ))}
            </ul>
          </article>

          <article
            className="rounded-2xl p-7"
            style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <div
              className="inline-flex items-center justify-center rounded-lg mb-4"
              style={{
                width: 40,
                height: 40,
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.10)',
              }}
            >
              <HiUserGroup style={{ width: 20, height: 20, color: '#F5F7FA' }} />
            </div>
            <div
              className="mono"
              style={{
                fontSize: 11,
                letterSpacing: 1.5,
                color: '#B4C1D8',
                textTransform: 'uppercase',
                marginBottom: 8,
              }}
            >
              {t('LoyaltyApp/Roles/employee_label')}
            </div>
            <h3
              className="font-semibold"
              style={{
                fontSize: 19,
                color: '#F5F7FA',
                margin: '0 0 14px',
                letterSpacing: '-0.01em',
              }}
            >
              {t('LoyaltyApp/Roles/employee_title')}
            </h3>
            <ul style={{ margin: '0 0 14px', padding: 0, listStyle: 'none' }}>
              {empCan.map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3"
                  style={{
                    padding: '6px 0',
                  }}
                >
                  <HiCheckCircle
                    className="flex-shrink-0"
                    style={{ width: 14, height: 14, color: '#A78BFA', marginTop: 4 }}
                  />
                  <span style={{ color: '#B4C1D8', fontSize: 13.5, lineHeight: 1.5 }}>{item}</span>
                </li>
              ))}
            </ul>
            <div
              className="mono"
              style={{
                fontSize: 11,
                letterSpacing: 1.5,
                color: '#FF4D4D',
                textTransform: 'uppercase',
                margin: '8px 0 8px',
                paddingTop: 12,
                borderTop: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              {t('LoyaltyApp/Roles/restricted_label')}
            </div>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {empCant.map((item, i) => (
                <li key={i} className="flex items-start gap-3" style={{ padding: '4px 0' }}>
                  <HiXCircle
                    className="flex-shrink-0"
                    style={{ width: 14, height: 14, color: '#FF4D4D', marginTop: 4 }}
                  />
                  <span style={{ color: '#6B7593', fontSize: 13.5, lineHeight: 1.5 }}>{item}</span>
                </li>
              ))}
            </ul>
          </article>
        </div>
      </div>
    </section>
  );
};

export default LoyaltyAppRoles;
